/**
 * The app's composition and behaviour: owns the stores, the connection and the voice chat,
 * turns server messages into state changes and offers the actions the screens call.
 *
 * Screens read state from the stores (signals) and call methods here; they never talk to
 * the network directly. Everything browser-specific is injected, so the whole flow can be
 * tested in Node with fakes.
 */
import {
  type ClientMessage,
  type ErrorCode,
  type IceServer,
  type PlayerProfile,
  PROTOCOL_VERSION,
  type RaceSettings,
  type RoomId,
  type RoomSnapshot,
  randomProfile,
  type ServerMessage,
  sameRaceSettings,
  sanitizeName,
} from '@cube-rush/shared';
import {
  batch,
  computed,
  effect,
  type ReadonlySignal,
  type Signal,
  signal,
  untracked,
} from '@preact/signals';
import { ru } from '../i18n/ru';
import { type AuthApi, createStatsApi, type LoginFailure, type StatsApi } from '../net/api';
import { canResend } from '../net/resend';
import type { HelloParams, NetPort } from '../net/types';
import type { AppUpdater, InstallPrompt } from '../pwa/types';
import { calendarSeason, type Season } from '../season/season';
import { ConnectionStore, type FatalReason } from '../state/connection';
import { RaceStore } from '../state/race';
import { RoomStore } from '../state/room';
import { SessionStore } from '../state/session';
import { SettingsStore } from '../state/settings';
import { StatsStore } from '../state/stats';
import type { KeyValueStorage } from '../state/storage';
import { ToastStore } from '../state/toasts';
import { UiStore } from '../state/ui';
import { raceSummary } from '../ui/race/raceSummary';
import type { VoiceController, VoiceControllerFactory } from '../voice/types';
import { type ScreenId, selectScreen } from './screen';

/** The toast about the next race: the host tapping through options updates one toast. */
const RACE_CHANGE_TOAST = 'raceSettings';

export interface AppControllerDeps {
  storage: KeyValueStorage;
  api: AuthApi;
  /** GET /api/stats (default: the real one, same origin). */
  statsApi?: StatsApi;
  /** Creates the connection; `hello` is evaluated on every (re)connect. */
  createNet: (hello: () => HelloParams | null) => NetPort;
  createVoice: VoiceControllerFactory;
  install: InstallPrompt;
  updater: AppUpdater;
  random?: () => number;
  /**
   * The holiday season of the calendar right now (the browser: the device's date, or the
   * `?season=` dev override). Default: by the device's date.
   */
  season?: () => Season;
}

/** Login problems the login form shows (server answers plus local checks). */
export type LoginError = LoginFailure | 'no_name' | 'no_password';

export class AppController {
  readonly session: SessionStore;
  readonly settings: SettingsStore;
  readonly connection = new ConnectionStore();
  readonly room: RoomStore;
  readonly race: RaceStore;
  readonly toasts = new ToastStore();
  readonly ui = new UiStore();
  readonly stats = new StatsStore();
  readonly net: NetPort;
  readonly voice: VoiceController;
  readonly install: InstallPrompt;
  readonly updater: AppUpdater;
  /** The screen to show; see selectScreen(). */
  readonly screen: ReadonlySignal<ScreenId>;

  private readonly api: AuthApi;
  private readonly statsApi: StatsApi;
  /** The statistics request on its way (one at a time), and the session it asks for. */
  private statsRequest: { token: string; done: Promise<void> } | null = null;
  private readonly random: () => number;
  private readonly seasonNow: () => Season;
  /** The season of the calendar (see refreshSeason); the settings decide whether it shows. */
  private readonly calendar: Signal<Season>;
  private readonly iceServers: Signal<readonly IceServer[]> = signal([]);
  /** After every welcome the outbox waits for the first room snapshot (see NetPort.flushOutbox). */
  private awaitingFirstRoom = false;
  /** The last race the player was taken into as a racer (see clearTheWayForRace). */
  private racingIn: string | null = null;
  private readonly disposers: Array<() => void> = [];

  constructor(deps: AppControllerDeps) {
    this.api = deps.api;
    this.statsApi = deps.statsApi ?? createStatsApi();
    this.random = deps.random ?? Math.random;
    this.install = deps.install;
    this.updater = deps.updater;
    this.seasonNow = deps.season ?? (() => calendarSeason(new Date()));
    this.calendar = signal(this.seasonNow());
    this.session = new SessionStore(deps.storage);
    this.settings = new SettingsStore(deps.storage, this.calendar);
    this.room = new RoomStore(this.session.playerId);
    this.race = new RaceStore(() => this.session.playerId.peek());
    this.net = deps.createNet(() => this.buildHello());
    this.voice = deps.createVoice({
      sendSignal: (to, data) => {
        this.net.send({ t: 'rtc', to, data });
      },
      iceServers: this.iceServers,
    });
    this.screen = computed(() =>
      selectScreen({
        fatal: this.connection.fatal.value,
        loggedIn: this.session.loggedIn.value,
        profileEditor: this.ui.profileEditor.value,
        race: this.race.current.value,
        watchingRaceId: this.ui.watchingRaceId.value,
        results: this.race.results.value,
        dismissedResultsId: this.ui.dismissedResultsId.value,
        statsOpen: this.ui.statsOpen.value,
      }),
    );

    this.disposers.push(
      this.net.onStatus((status) => this.connection.setStatus(status)),
      this.net.onMessage((message) => this.handleMessage(message)),
      effect(() => {
        const volume = this.settings.settings.value.voiceVolume;
        untracked(() => this.voice.setMasterVolume(volume));
      }),
      effect(() => this.syncVoicePeers()),
      effect(() => this.reportMicState()),
      effect(() => this.clearTheWayForRace()),
      effect(() => this.watchWhenDropped()),
      effect(() => this.watchConnectionWhileRacing()),
    );
  }

  /**
   * Looks at the calendar again (the app may stay open across midnight into a holiday or out
   * of one). Cheap; the browser app calls it every hour and when the page comes back.
   */
  refreshSeason(): void {
    this.calendar.value = this.seasonNow();
  }

  /** Connects if a session is stored (returning player). */
  start(): void {
    if (this.session.loggedIn.peek() && this.connection.fatal.peek() === null) this.net.start();
  }

  dispose(): void {
    for (const dispose of this.disposers.splice(0)) dispose();
    this.net.dispose();
    this.voice.dispose();
  }

  // ---- account ----

  /** Logs in and connects. Resolves to null on success, otherwise the problem to show. */
  async login(password: string, rawName: string): Promise<LoginError | null> {
    const name = sanitizeName(rawName);
    if (name === '') return 'no_name';
    if (password === '') return 'no_password';
    // The device's stable key: statistics follow it from this session to the next.
    const result = await this.api.login({ password, name, key: this.session.playerKey() });
    if (!result.ok) return result.error;

    const stored = this.session.profile.peek();
    const profile: PlayerProfile = stored ? { ...stored, name } : randomProfile(name, this.random);
    const { token, playerId, expiresAt } = result.session;
    batch(() => {
      this.session.signIn({ token, playerId, expiresAt }, profile);
      this.ui.setLoginNotice(null);
      // A new player picks their look first; returning players go straight to the room.
      if (!stored) this.ui.openProfileEditor('first-run');
    });
    this.net.start();
    return null;
  }

  logout(): void {
    this.leaveVoice();
    // Best effort, so the seat is freed at once instead of showing "no connection" for the
    // reconnect grace period (and holding up a running race). Offline it is not queued:
    // there is no connection to say goodbye on, and the seat expires on its own.
    this.net.send({ t: 'bye' });
    this.net.stop();
    batch(() => {
      this.session.signOut();
      this.room.clear();
      this.race.clear();
      this.stats.clear();
      this.statsRequest = null;
      this.ui.reset();
      this.connection.setFatal(null);
    });
  }

  /** Saves the look locally and tells the room (when offline, the next `hello` carries it). */
  saveProfile(profile: PlayerProfile): void {
    this.session.setProfile(profile);
    this.net.send({ t: 'profile', profile });
  }

  // ---- room ----

  setReady(ready: boolean): void {
    this.sendOrWarn({ t: 'room.ready', ready });
  }

  /**
   * Host only: start the race now. The server races the ready members plus the host
   * (everybody when nobody is ready). Older servers raced only the ready members, so a host
   * who has not pressed "ready" while others have still gets ready first: needed there,
   * harmless now. If that makes everybody ready, the server starts on its own and a start
   * request on top would only be refused.
   */
  startRace(): void {
    const me = this.room.me.peek();
    const others = this.room.members.peek().filter((m) => m.connected && m.id !== me?.id);
    if (me && !me.ready && others.some((m) => m.ready)) {
      if (!this.sendOrWarn({ t: 'room.ready', ready: true })) return;
      if (others.every((m) => m.ready)) return;
    }
    this.sendOrWarn({ t: 'room.start' });
  }

  /**
   * The host sets up the next race (between races). Only what the tap changed is sent: the
   * server applies the patches in order, so two quick taps («На время», then «10 мин») never
   * undo each other. The room's settings come back in its next snapshot.
   */
  changeRaceSettings(patch: Partial<RaceSettings>): boolean {
    return this.sendOrWarn({ t: 'room.settings', settings: patch });
  }

  requestRooms(): void {
    this.net.send({ t: 'rooms.list' });
  }

  createRoom(rawName: string): boolean {
    const name = sanitizeName(rawName);
    return name !== '' && this.sendOrWarn({ t: 'room.create', name });
  }

  joinRoom(roomId: RoomId): void {
    this.sendOrWarn({ t: 'room.join', roomId });
  }

  /** Back to the default room. */
  leaveRoom(): void {
    this.sendOrWarn({ t: 'room.leave' });
  }

  // ---- race ----

  /**
   * Leaves the game screen: a racer gives up the race (and may watch it later from the
   * room), a spectator just stops watching. Once the time of a race against the clock is up
   * there is nothing left to give up: the screen is only left (the server would ignore it).
   */
  leaveGame(): void {
    const current = this.race.current.peek();
    if (!current) return;
    const { endAt, raceId } = current.race;
    const timeUp = endAt !== null && this.net.serverNow() >= endAt;
    if (current.role === 'racer' && !timeUp) {
      // Reliable: a quit sent during a connection blip must still reach the server.
      this.net.sendReliable({ t: 'race.quit' }, raceId);
    }
    batch(() => {
      this.race.markQuit();
      this.ui.watchRace(null);
    });
  }

  /** Opens the running race as a spectator. */
  watchRace(): void {
    const current = this.race.current.peek();
    if (current) this.ui.watchRace(current.race.raceId);
  }

  closeResults(): void {
    const results = this.race.results.peek();
    if (results) this.ui.dismissResults(results.raceId);
  }

  showResults(): void {
    this.ui.showResults();
  }

  /** "Ещё раз!": ready for the next race and back to the room. */
  playAgain(): void {
    this.setReady(true);
    this.closeResults();
  }

  // ---- statistics ----

  /** Opens the statistics screen and fetches the latest numbers. */
  openStats(): void {
    this.ui.openStats();
    void this.loadStats();
  }

  closeStats(): void {
    this.ui.closeStats();
  }

  /**
   * Fetches the statistics (the shown ones stay until the new ones are in). An answer that
   * comes after its session ended (logged out, maybe somebody else logged in on the device) is
   * dropped: nobody sees another session's numbers and «ты».
   */
  loadStats(): Promise<void> {
    const auth = this.session.auth.peek();
    if (!auth) return Promise.resolve();
    const pending = this.statsRequest;
    if (pending?.token === auth.token) return pending.done;
    this.stats.loading();
    const { token } = auth;
    const done = this.statsApi
      .stats(token)
      .then((result) => {
        if (this.session.auth.peek()?.token !== token) return;
        if (result.ok) this.stats.loaded(result.stats);
        else this.stats.failed(result.error);
      })
      .finally(() => {
        if (this.statsRequest?.done === done) this.statsRequest = null;
      });
    this.statsRequest = { token, done };
    return done;
  }

  // ---- platform ----

  /** Native install prompt where the browser has one, otherwise the iOS how-to. */
  async promptInstall(): Promise<void> {
    if (this.install.mode.peek() === 'ios-guide') {
      this.ui.openDialog('installGuide');
      return;
    }
    if (await this.install.prompt()) {
      this.toasts.show(ru.install.installed, { tone: 'success' });
    }
  }

  // ---- recovery ----

  /** "Играть здесь" after the session was taken over by another device. */
  takeOver(): void {
    this.connection.setFatal(null);
    this.net.start();
  }

  // ---- server messages ----

  private handleMessage(message: ServerMessage): void {
    switch (message.t) {
      case 'welcome':
        this.handleWelcome(message);
        return;
      case 'error':
        this.handleError(message.code);
        return;
      case 'rooms':
        this.room.setRooms(message.rooms);
        return;
      case 'room':
        this.handleRoom(message.room);
        return;
      case 'room.left':
        this.handleRoomLeft(message.reason);
        return;
      case 'race.start':
        this.race.handleRaceStart(message);
        return;
      case 'race.progress':
        this.race.handleProgress(message);
        return;
      case 'race.end':
        batch(() => {
          this.race.handleRaceEnd(message);
          this.ui.watchRace(null);
        });
        return;
      case 'rtc':
        this.voice.handleSignal(message.from, message.data);
        return;
      case 'pong':
      case 'race.states':
      case 'race.event':
        // Handled by NetClient (clock) and by the game screen's net bridge.
        return;
      default:
        message satisfies never;
    }
  }

  private handleWelcome(message: Extract<ServerMessage, { t: 'welcome' }>): void {
    if (message.v !== PROTOCOL_VERSION) {
      this.fail('version');
      return;
    }
    this.awaitingFirstRoom = true;
    this.iceServers.value = message.iceServers;
    const auth = this.session.auth.peek();
    if (auth && auth.playerId !== message.you.id) {
      this.session.signIn({ ...auth, playerId: message.you.id }, message.you.profile);
    } else {
      // The server sanitizes names; adopt its version so everybody sees the same.
      this.session.setProfile(message.you.profile);
    }
  }

  private handleError(code: ErrorCode): void {
    switch (code) {
      case 'unauthorized':
        this.logout();
        this.ui.setLoginNotice(ru.login.sessionExpired);
        return;
      case 'version_mismatch':
        this.fail('version');
        return;
      default:
        this.toasts.show(ru.errors[code], { tone: 'error' });
    }
  }

  private handleRoom(room: RoomSnapshot): void {
    const previous = this.room.room.peek();
    // The first snapshot after a (re)connect is how things are, not news.
    const reconnected = this.awaitingFirstRoom;
    batch(() => {
      if (previous && previous.id !== room.id) {
        // Another room: nothing of the old room's races applies any more.
        this.race.clear();
        this.ui.watchRace(null);
      }
      const resultsKnown = this.race.results.peek()?.raceId === room.race?.raceId;
      this.room.setRoom(room);
      this.race.handleRoom(room);
      if (!resultsKnown) this.hideResultsFromNewcomer(room);
    });
    this.session.setLastRoomId(room.id);
    if (previous?.id === room.id && !reconnected) this.announceRaceChange(previous, room);
    if (this.awaitingFirstRoom) {
      this.awaitingFirstRoom = false;
      const running = room.phase === 'countdown' || room.phase === 'race';
      const raceId = running ? room.race?.raceId : undefined;
      // Only events of the race still running count, and a message that may have arrived
      // before the connection broke goes again only if the server ignores duplicates.
      this.net.flushOutbox(
        (queued) => queued.tag === raceId && (!queued.maybeDelivered || canResend(queued.message)),
      );
    }
  }

  /**
   * The host changed the next race: everybody else hears what it is now (the server took their
   * «на старте», nobody is pulled into a race they did not see). One toast, updated in place,
   * however many options the host taps through.
   */
  private announceRaceChange(previous: RoomSnapshot, room: RoomSnapshot): void {
    if (sameRaceSettings(previous.settings, room.settings)) return;
    const myId = this.session.playerId.peek();
    if (myId === null || room.hostId === myId) return;
    this.toasts.show(ru.race.changed(raceSummary(room.settings)), { key: RACE_CHANGE_TOAST });
  }

  /**
   * Results pop up for the players who saw the race end. Somebody who walks into a room
   * showing the results of a race they did not take part in gets the room instead (the
   * results stay one tap away there).
   */
  private hideResultsFromNewcomer(room: RoomSnapshot): void {
    const myId = this.session.playerId.peek();
    if (room.phase !== 'results' || !room.race) return;
    if (room.results?.some((entry) => entry.id === myId)) return;
    this.ui.dismissResults(room.race.raceId);
  }

  private handleRoomLeft(reason: 'left' | 'closed' | 'replaced'): void {
    if (reason === 'replaced') {
      this.fail('replaced');
      return;
    }
    batch(() => {
      this.room.clear();
      this.race.clear();
      this.ui.watchRace(null);
    });
    if (reason === 'closed') this.toasts.show(ru.toasts.roomClosed, { tone: 'warning' });
  }

  // ---- helpers ----

  private buildHello(): HelloParams | null {
    const auth = this.session.auth.peek();
    const profile = this.session.profile.peek();
    if (!auth || !profile) return null;
    const hello: HelloParams = { token: auth.token, profile };
    const roomId = this.session.lastRoomId.peek();
    if (roomId) hello.roomId = roomId;
    // The race on screen goes on through a reconnect: its levels (up to 30 KB) need not come
    // again ahead of the positions. A reloaded page has none and gets them.
    const raceId = this.race.current.peek()?.race.raceId;
    if (raceId) hello.raceId = raceId;
    return hello;
  }

  /** Stops the connection and shows a blocking screen until the player acts. */
  private fail(reason: FatalReason): void {
    this.leaveVoice();
    this.net.stop();
    batch(() => {
      this.room.clear();
      this.race.clear();
      this.ui.closeDialog();
      this.connection.setFatal(reason);
    });
  }

  private leaveVoice(): void {
    const myId = this.session.playerId.peek();
    if (myId) this.voice.syncPeers([], myId);
  }

  /** Sends a room action, or tells the player that there is no connection right now. */
  private sendOrWarn(message: ClientMessage): boolean {
    const sent = this.net.send(message);
    if (!sent) this.toasts.show(ru.connection.offline, { tone: 'warning' });
    return sent;
  }

  /** Effect: the voice engine follows the connected members of the room. */
  private syncVoicePeers(): void {
    const myId = this.session.playerId.value;
    const ids = this.room.members.value.filter((m) => m.connected).map((m) => m.id);
    if (myId) untracked(() => this.voice.syncPeers(ids, myId));
  }

  /**
   * Effect: a race the player takes part in gets the screen at once. Its clock runs from the
   * end of the short countdown whether the player looks or not, so the profile editor, the
   * statistics and any open dialog are closed instead of hiding the game (settings can be opened
   * again from inside the game).
   */
  private clearTheWayForRace(): void {
    const current = this.race.current.value;
    if (current?.role !== 'racer' || current.race.raceId === this.racingIn) return;
    this.racingIn = current.race.raceId;
    untracked(() =>
      batch(() => {
        this.ui.closeProfileEditor();
        this.ui.closeDialog();
        this.ui.closeStats();
      }),
    );
  }

  /**
   * Effect: a racer who was away too long (the seat's grace ran out, or everybody else was done
   * while the app was in the background) is out of the race on the server, which ignores
   * their run from then on. Instead of racing on for nothing, the game turns into watching
   * the rest of the race, and a toast says why.
   */
  private watchWhenDropped(): void {
    const current = this.race.current.value;
    const progress = this.race.progress.value;
    if (current?.role !== 'racer') return;
    const myId = this.session.playerId.peek();
    if (!progress.some((entry) => entry.id === myId && entry.quit)) return;
    untracked(() =>
      batch(() => {
        this.race.markQuit();
        this.ui.watchRace(current.race.raceId);
        this.toasts.show(ru.toasts.droppedFromRace, { tone: 'warning' });
      }),
    );
  }

  /**
   * Effect: while the player races, a dead connection is noticed sooner (NetPort.setRacing): the
   * others see the racer again and the finished levels reach the server that much sooner.
   */
  private watchConnectionWhileRacing(): void {
    const racing = this.race.current.value?.role === 'racer';
    untracked(() => this.net.setRacing(racing));
  }

  /** Effect: the server shows a crossed-out mic for muted players and players without a mic. */
  private reportMicState(): void {
    const muted = this.voice.muted.value || !this.voice.micAvailable.value;
    if (this.connection.status.value === 'online') {
      untracked(() => this.net.send({ t: 'voice', muted }));
    }
  }
}
